import React, { useEffect, useMemo, useState, useCallback, useLayoutEffect, useRef } from "react";
import axios from "axios";
import "./Dashboard.css";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, PieChart, Pie, Cell } from "recharts";
import {
  Container, Grid, Card, CardTitle, CardSubtitle, WelcomeMessage, CenteredNumber,
} from "./Styles";
import { CircleCheckBig, CircleDashed, MessageCircle, MessageCirclePlus, ChevronLeft, ChevronRight } from "lucide-react";


/* ===== Tema & utils ===== */
const TITLE_ACCENT = "#FFC107";
const POSITIVE = "#7CB342";
const NEGATIVE = "#EF5350";
const TOOLTIP_STYLE = { background: "#2a2a37", border: "none", borderRadius: 8, color: "#fff" };
const STAGE_ORDER = ["Contato Inicial","Qualificação","Acompanhamento","Agendamento","Agendado","Cliente"];
const fmtInt = (n) => (isFinite(n) ? Math.round(n).toLocaleString("pt-BR") : "0");
const clampPct = (v) => Math.max(0, Math.min(100, Number(v || 0)));
const fmtPP = (v) => `${(Number(v)||0).toFixed(1)} pp`;
const PAGE_SIZE_ACTIONS = 10;


// === Helpers Meta ===
const MSG_KEYS = [
  "onsite_conversion.messaging_conversation_started_7d",
  // fallback opcional:
  "messaging_conversation_started_7d",
];


function useMasonryRows(deps = []) {
  const gridRef = useRef(null);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    // pega valores reais do CSS (gap e altura-base da linha)
    const styles = getComputedStyle(grid);
    const rowGap = parseFloat(styles.getPropertyValue("row-gap")) || parseFloat(styles.getPropertyValue("gap")) || 0;
    const rowSize = parseFloat(styles.getPropertyValue("grid-auto-rows")) || 8;

    

    // função que aplica o span a todos os itens
    const layout = () => {
      const items = grid.querySelectorAll(".draggable");
      items.forEach((el) => {
        // mede a altura total do item (inclui bordas/padding do Card)
        const h = el.getBoundingClientRect().height;
        // calcula quantas “linhas-base” cabem nesta altura
        const span = Math.max(1, Math.ceil((h + rowGap) / (rowSize + rowGap)));
        el.style.gridRowEnd = `span ${span}`;
      });
    };

    // observa mudanças de tamanho do grid e dos filhos (Recharts, etc.)
    const ro = new ResizeObserver(() => {
      // evita jank: mede no próximo frame
      requestAnimationFrame(layout);
    });

    ro.observe(grid);
    grid.querySelectorAll(".draggable").forEach((el) => ro.observe(el));

    // mede na primeira montagem
    layout();

    // re-meça ao redimensionar a janela
    const onResize = () => requestAnimationFrame(layout);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps); // sempre que “deps” mudar, recalculamos

  return gridRef;
}

function num(v){ const n = Number(v); return isFinite(n) ? n : 0; }
function pickAction(actions, keys = MSG_KEYS){
  if (!Array.isArray(actions)) return 0;
  return actions.reduce((s,a)=>{
    const t = (a.action_type || a.action||"").toString();
    if (keys.some(k => t === k)) s += num(a.value);
    return s;
  }, 0);
}

// soma diário (por data_start) para a métrica desejada
function buildDailySeries(metaDaily, field = "spend"){
  const out = new Map();
  const rows = metaDaily?.data || [];
  for (const r of rows){
    const d = r.date_start || r.date || r.date_stop;
    const v = r[field] !== undefined ? num(r[field]) : null;
    if (!d || v === null) continue;
    out.set(d, (out.get(d) || 0) + v);
  }
  // retorna [{x:"YYYY-MM-DD", y:Number}]
  return [...out.entries()]
    .sort((a,b)=>a[0].localeCompare(b[0]))
    .map(([x,y])=>({ x, y }));
}

// tenta montar “ações por dia” se o backend trouxer actions no diário
function buildDailyMessages(metaDaily){
  const out = new Map();
  const rows = metaDaily?.data || [];
  for (const r of rows){
    const d = r.date_start || r.date;
    if (!d) continue;
    const q = pickAction(r.actions);
    if (q > 0) out.set(d, (out.get(d)||0) + q);
  }
  return [...out.entries()].sort((a,b)=>a[0].localeCompare(b[0])).map(([x,y])=>({x,y}));
}

// --- Normalizador p/ "Leads por origem" (aceita array OU { total, rows })
function normalizeLeadsPorOrigem(raw) {
  if (!raw) return { total: 0, rows: [] };

  const toRow = (r) => ({
    name: r.name || r.origem || r.origem_lead || "(Sem origem)",
    count: Number(r.count ?? r.value ?? r.quantidade ?? 0),
    prev_count: Number(r.prev_count ?? 0),
    pct_total: Number(r.pct_total ?? 0),
    delta_count_pct: Number(r.delta_count_pct ?? 0),
  });

  if (Array.isArray(raw)) {
    const rows = raw.map(toRow);
    const total = rows.reduce((s, r) => s + (r.count || 0), 0);
    return { total, rows };
  }

  const rows = Array.isArray(raw.rows) ? raw.rows.map(toRow) : [];
  const total = Number(raw.total ?? rows.reduce((s, r) => s + (r.count || 0), 0));
  return { total, rows };
}


/* ===== Cards concretos ===== */
function KpiCard({ title, value, delta, series, wrapperProps = {} }) {
  return (
    <Card {...wrapperProps}>
      <CardTitle style={{ color: TITLE_ACCENT }}>{title}</CardTitle>
      <CardSubtitle>Mês corrente</CardSubtitle>
<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
  <CenteredNumber style={{ margin: 0 /* opcional: textAlign:'left' ou 'center' */ }}>
    {value}
  </CenteredNumber>
  <div style={{ width: "100%", height: 60 }}>
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={series}>
        <XAxis hide dataKey="x" />
        <YAxis hide />
        <RTooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [fmtInt(v), ""]} />
        <Line type="monotone" dataKey="y" stroke="#29B6F6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
      </LineChart>
    </ResponsiveContainer>
  </div>
</div>

      <div style={{ marginTop: 8, fontWeight: 800, fontSize: 20, color: (delta ?? 0) >= 0 ? POSITIVE : NEGATIVE }}>
        {(delta ?? 0) >= 0 ? "▲" : "▼"} {isFinite(delta) ? delta.toFixed(1) : 0}%
      </div>
    </Card>
  );
}

function Tip({ text }) { return <span className="tip" data-tip={text}>?</span>; }
function Bar({ pct }) {
  const real = Number(pct || 0);
  const label = real > 100 ? "100%+" : `${clampPct(real).toFixed(0)}%`;
  const tip = real > 100 ? `Valor real: ${real.toFixed(1)}%.\nA barra é limitada a 100% apenas na estética.` : "";
  return (
    <div className={`ladder__bar ${real > 100 ? "has-tip" : ""}`} data-tip={tip}>
      <div className="ladder__bar-bg" />
      <div className="ladder__bar-fill" style={{ width: `${clampPct(real)}%` }} />
      <div className="ladder__bar-label">{label}</div>
    </div>
  );
}
function ChipDelta({ value }) {
  const up = (value ?? 0) >= 0;
  return (
    <span className={`chip ${up ? "chip--up" : "chip--down"}`}>
      {up ? "▲" : "▼"} {fmtPP(value)}
    </span>
  );
}
function FunnelLadderCard({ funnel, wrapperProps = {} }) {
  const stagesOrdered = useMemo(() => {
    const map = new Map((funnel?.stages || []).map(s => [s.name, s]));
    return STAGE_ORDER.map(n => map.get(n)).filter(Boolean);
  }, [funnel]);
  if (!funnel || !stagesOrdered.length) {
    return (
      <Card {...wrapperProps}>
        <CardTitle style={{ color: TITLE_ACCENT }}>Leads por Estágio</CardTitle>
        <div className="muted">Sem dados para o período.</div>
      </Card>
    );
  }
  const sum = funnel.summary;
  return (
    <Card {...wrapperProps}>
      <CardTitle style={{ color: TITLE_ACCENT }}>Leads por Estágio (Ladder)</CardTitle>
      <div className="ladder__summary">
        <div>
          Conversão topo → cliente:
          <strong> {sum.conv_top_to_bottom.toFixed(1)}%</strong>{" "}
          <span className="muted">({sum.prev_conv_top_to_bottom.toFixed(1)}% mês ant.)</span>{" "}
          <ChipDelta value={sum.delta_conv_pp} />
        </div>
        <div className="muted">Topo: {sum.top_count} • Cliente: {sum.client_count}</div>
        {sum.worst_stage && (
          <div className="ladder__hint">
            Gargalo: <strong>{sum.worst_stage.name}</strong>{" "}
            <span className="muted">({sum.worst_stage.pct_prev.toFixed(1)}%)</span>{" "}
            <ChipDelta value={sum.worst_stage.delta_pp} />
          </div>
        )}
      </div>
      <div className="ladder">
        <div className="ladder__head">
          <span>Estágio</span>
          <span className="center">
            da etapa anterior <Tip text={"Taxa que avança da etapa anterior para esta.\nPode ficar acima de 100% quando o lead pula etapas ou entra direto.\nA barra limita a 100%, o texto mostra o valor real."} />
          </span>
          <span className="right">
            % do topo <Tip text={"Participação da etapa em relação ao total que entrou no topo no período."} />
          </span>
        </div>
        {stagesOrdered.map((s) => (
          <div className="ladder__row" key={s.name}>
            <div className="ladder__stage">
              <div className="ladder__stage-name">{s.name}</div>
              <div className="ladder__counts">
                {fmtInt(s.count)}
                <span className="muted">&nbsp;({fmtInt(s.prev_count)} ant.)</span>
                <span className={`chip chip--mini ${(s.delta_count_pct||0) >= 0 ? "chip--up" : "chip--down"}`}>
                  {(s.delta_count_pct||0) >= 0 ? "▲" : "▼"} {Math.abs(s.delta_count_pct||0).toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="ladder__cell center">
              <Bar pct={s.pct_prev} />
              <div className="muted">
                {s.prev_pct_prev.toFixed(1)}% ant. <ChipDelta value={s.delta_prev_pp} />
              </div>
            </div>
            <div className="ladder__cell right">
              <Bar pct={s.pct_top} />
              <div className="muted">
                {s.prev_pct_top.toFixed(1)}% ant. <ChipDelta value={s.delta_top_pp} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ===== Card 3: Ações de hoje ===== */
function ActionsTodayCard({ acoes, onConcluir, wrapperProps = {} }) {
  // pendentes primeiro; depois alfabético pelo primeiro nome
  const itemsAll = (acoes?.items || []).slice().sort((a,b) => {
    const byDone = (a.is_done ? 1 : 0) - (b.is_done ? 1 : 0);
    if (byDone !== 0) return byDone;
    return (a.lead_first || "").localeCompare(b.lead_first || "", "pt-BR", {sensitivity:"base"});
  });

  const counts = acoes?.counts || { pendentes:0, concluidas:0, total:0 };

  const [page, setPage] = useState(1);
  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(itemsAll.length / pageSize));
  const pageItems = itemsAll.slice((page-1)*pageSize, page*pageSize);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages]);

  return (
    <Card {...wrapperProps}>
      <CardTitle style={{ color: TITLE_ACCENT, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <span>Ações de hoje</span>
        <div className="actions__pager">
          <button className="pager__btn" onClick={() => setPage(p => Math.max(1,p-1))} disabled={page<=1}><ChevronLeft className="icone-pagina" strokeWidth={2.25}/></button>
          <span className="pager__badge">{page}</span>
          <button className="pager__btn" onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page>=totalPages}><ChevronRight className="icone-pagina" strokeWidth={2.25}/></button>
        </div>
      </CardTitle>

      <div className="actions__chips">
        <span className="chip chip--muted">{counts.pendentes} pendentes</span>
        <span className="chip chip--muted">{counts.concluidas} concluídas</span>
        <span className="chip chip--muted">{counts.total} no total</span>
      </div>

      <div className="actions__list">
        {pageItems.map((a) => {
          const done = !!a.is_done;
          return (
            <div key={a.id} className={`action__row ${done ? "is-done" : "is-pending"}`}>
              {/* overlay por cima de tudo para abrir o chat */}
              <a
                className={`action__overlay ${a.chat_url ? "" : "is-disabled"}`}
                href={a.chat_url || "#"}
                target="_blank"
                rel="noreferrer"
                aria-label="Abrir conversa"
              />
              <div className="action__left">
                <MessageCirclePlus className="action__icon icone-msg" strokeWidth={2.25} />

                <div className="action__text">
                  <div className="action__title">
                    <strong>{a.lead_first || "Lead"}</strong>
                    <span className={`badge ${done ? "badge--done" : "badge--pendente"}`}>
                      {done ? "Concluída" : "Pendente"}
                    </span>
                  </div>
                  <div className="action__desc">{a.descricao || "—"}</div>
                </div>
              </div>

              <button
                className="action__btn-done"
                title="Concluir ação"
                disabled={done}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onConcluir?.(a.id); }}
              >
                <CircleCheckBig className="icone-concluida" strokeWidth={2.25} />
              </button>
            </div>
          );
        })}
        {!pageItems.length && <div className="muted">Sem ações para hoje.</div>}
      </div>
    </Card>
  );
}

/* CARD $ - Ações atrasadas */
/* CARD $ - Ações atrasadas (sem chip de pendentes; data ao lado do badge) */
function ActionsLateCard({ acoes, onConcluir, wrapperProps = {} }) {
  const all = (acoes?.items || []).slice().sort((a,b) => {
    // data mais antiga primeiro; depois nome
    const da = new Date(a.data_acao), db = new Date(b.data_acao);
    if (da.getTime() !== db.getTime()) return da - db;
    return (a.lead_first || "").localeCompare(b.lead_first || "", "pt-BR", {sensitivity:"base"});
  });
  const counts = acoes?.counts || { total: 0 };

  // paginação por itens
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(all.length / pageSize));
  const pageItems = all.slice((page-1)*pageSize, page*pageSize);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages]);

  return (
    <Card {...wrapperProps}>
      <CardTitle style={{ color: TITLE_ACCENT, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <span>Ações atrasadas</span>
        <div className="actions__pager">
          <button className="pager__btn" onClick={() => setPage(p => Math.max(1,p-1))} disabled={page<=1}>
            <ChevronLeft className="icone-pagina" strokeWidth={2.25}/>
          </button>
          <span className="pager__badge">{page}</span>
          <button className="pager__btn" onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page>=totalPages}>
            <ChevronRight className="icone-pagina" strokeWidth={2.25}/>
          </button>
        </div>
      </CardTitle>

      {/* Só o total (sem "pendentes") */}
      <div className="actions__chips">
        <span className="chip chip--muted">{counts.total} no total</span>
      </div>

      <div className="actions__list">
        {pageItems.map((a) => (
          <div key={a.id} className="action__row is-pending">
            <a
              className={`action__overlay ${a.chat_url ? "" : "is-disabled"}`}
              href={a.chat_url || "#"}
              target="_blank"
              rel="noreferrer"
              aria-label="Abrir conversa"
            />
            <div className="action__left">
              <span className={`overdue-dot ${a.delay_bucket}`} title={`${a.dias_atraso} dia(s) de atraso`} />
              <MessageCirclePlus className="action__icon icone-msg" strokeWidth={2.25} />
              <div className="action__text">
                <div className="action__title">
                  <strong>{a.lead_first || "Lead"}</strong>
                  <span className="badge badge--pendente">Pendente</span>
                  {/* data ao lado do badge */}
                  <span className="badge badge--date">{a.data_label}</span>
                </div>
                <div className="action__desc">
                  {a.descricao || "—"} • {a.dias_atraso} dia(s) em atraso
                </div>
              </div>
            </div>

            <button
              className="action__btn-done"
              title="Concluir ação"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onConcluir?.(a.id); }}
            >
              <CircleCheckBig className="icone-concluida" strokeWidth={2.25} />
            </button>
          </div>
        ))}
        {!pageItems.length && <div className="muted">Sem ações atrasadas 🎉</div>}
      </div>
    </Card>
  );
}


/* ===== Card: Leads por origem ===== */
/* ===== Normalizador p/ "Leads por origem" (aceita array OU {total, rows}) ===== */
/* ========= CARD: Leads por origem ========= */
function LeadsBySourceCard({ data, wrapperProps = {} }) {
  const { total, rows } = useMemo(() => normalizeLeadsPorOrigem(data), [data]);

  // Top N + “Outras”
  const TOP_N = 7;
  const sorted = rows.slice().sort((a, b) => (b.count || 0) - (a.count || 0));
  const top = sorted.slice(0, TOP_N);
  const rest = sorted.slice(TOP_N);

  const outrasCount = rest.reduce((s, r) => s + (r.count || 0), 0);
  const outrasPrev  = rest.reduce((s, r) => s + (r.prev_count || 0), 0);

  if (outrasCount > 0) {
    top.push({
      name: "Outras",
      count: outrasCount,
      prev_count: outrasPrev,
      pct_total: total > 0 ? (outrasCount / total) * 100 : 0,
      delta_count_pct: outrasPrev > 0 ? ((outrasCount - outrasPrev) / outrasPrev) * 100 : 0,
    });
  }

  if (!total || !sorted.length) {
    return (
      <Card {...wrapperProps}>
        <CardTitle style={{ color: "#FFC107" }}>Leads por origem</CardTitle>
        <div className="muted">Sem dados para o período.</div>
      </Card>
    );
  }

  return (
    <Card {...wrapperProps}>
      <CardTitle style={{ color: "#FFC107" }}>Leads por origem</CardTitle>
      <div className="origins__list">
        {top.map((r) => {
          const pct = Math.max(0, Math.min(100, Number(r.pct_total || 0)));
          return (
            <div key={r.name} className="origin__row">
              <div className="origin__left">
                <div className="origin__name" title={r.name}>{r.name}</div>
                <span className="chip chip--mini">{(r.count || 0).toLocaleString("pt-BR")}</span>
                <span className="muted">{pct.toFixed(1)}%</span>
                <ChipDelta value={r.delta_count_pct} />
              </div>
              <div className="origin__bar">
                <div className="origin__bar-fill" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="origins__footer muted">
        Total: <strong>{total.toLocaleString("pt-BR")}</strong> leads
        <Tip text={"% indica participação de cada origem no total do período.\nVariação compara com o mesmo intervalo do mês anterior (parcial)."} />
      </div>
    </Card>
  );
}

/* ========= Normalizador genérico para linhas simples ========= */
function normalizeRows(raw) {
  if (!raw) return { total: 0, rows: [] };

  const toRow = (r) => ({
    name: r.name || r.tag || r.rotulo || "(Sem tag)",
    count: Number(r.count ?? r.value ?? r.quantidade ?? 0),
    prev_count: Number(r.prev_count ?? 0),
    pct_total: Number(r.pct_total ?? 0),
    delta_count_pct: Number(r.delta_count_pct ?? 0),
  });

  if (Array.isArray(raw)) {
    const rows = raw.map(toRow);
    const total = rows.reduce((s, r) => s + (r.count || 0), 0);
    return { total, rows };
  }

  const rows = Array.isArray(raw.rows) ? raw.rows.map(toRow) : [];
  const total = Number(raw.total ?? rows.reduce((s, r) => s + (r.count || 0), 0));
  return { total, rows };
}

/* ========= Normalizador específico (tags) ========= */
function normalizeTags(raw) {
  if (!raw) return { total: 0, rows: [] };

  const toRow = (r) => ({
    name: r.name || r.tag || r.label || "(Sem tag)",
    count: Number(r.count ?? r.value ?? 0),
    prev_count: Number(r.prev_count ?? 0),
    delta_pp:
      r.delta_pp !== undefined
        ? Number(r.delta_pp)
        : (r.prev_count > 0 ? ((r.count - r.prev_count) / r.prev_count) * 100 : 0),
  });

  const rows = Array.isArray(raw)
    ? raw.map(toRow)
    : (Array.isArray(raw.rows) ? raw.rows.map(toRow) : []);

  const total = Number(raw.total ?? rows.reduce((s, r) => s + (r.count || 0), 0));
  return { total, rows };
}

/* ========= Paleta elegante e única por tag ========= */
const OTHER_COLOR = "rgba(255,255,255,.28)"; // “Outras” neutro discreto

function makeElegantPalette(n) {
  // Espaçamento por ângulo dourado — bom contraste e sem “arco-íris infantil”
  const GOLDEN = 137.508;
  const SAT = 58;   // saturação moderada
  const L0  = 52;   // luminosidade base
  const out = [];
  let startHue = 210; // base fria (ajuste se quiser)
  for (let i = 0; i < n; i++) {
    const h = (startHue + i * GOLDEN) % 360;
    out.push(`hsl(${h} ${SAT}% ${L0 + (i % 2 ? 6 : 0)}%)`);
  }
  return out;
}

/* ========= Utils comuns ========= */
function fmt(n){ return isFinite(n) ? Math.round(n).toLocaleString("pt-BR") : "0"; }
const pp = (v) => `${(Number(v) || 0).toFixed(1)} pp`;

/* ========= Dimensões do donut ========= */
const DONUT = {
  height: 240,   // altura do gráfico
  inner: 70,     // raio interno (maior = mais “fino” no centro)
  outer: 98,     // raio externo (maior = gráfico maior e mais “grosso”)
  strokeW: 1.25, // borda entre as fatias
};

/* ========= CARD: Leads por TAG (donut Top 8 + Outras, sem legenda à esquerda) ========= */
export function LeadsByTagCard({ data, wrapperProps = {} }) {
  const { total, rows } = React.useMemo(() => normalizeTags(data), [data]);

  // top 8 + Outras
  const sorted = rows.slice().sort((a, b) => (b.count || 0) - (a.count || 0));
  const TOP = 8;
  const top = sorted.slice(0, TOP);
  const rest = sorted.slice(TOP);

  if (rest.length) {
    const c = rest.reduce((s, r) => s + (r.count || 0), 0);
    const p = rest.reduce((s, r) => s + (r.prev_count || 0), 0);
    top.push({ name: "Outras", count: c, prev_count: p, delta_pp: p > 0 ? ((c - p) / p) * 100 : 0 });
  }

  // Paleta única por tag (fixa por nome)
  const base = top.filter(r => r.name !== "Outras");
  const palette = makeElegantPalette(base.length);
  const colorMap = new Map(base.map((r, i) => [r.name, palette[i]]));
  const getColor = (name) => (name === "Outras" ? OTHER_COLOR : (colorMap.get(name) || palette[0]));

  const pieData = top.map(r => ({ name: r.name, value: r.count, color: getColor(r.name) }));
  const [active, setActive] = React.useState(-1);
  const activeRow = active >= 0 ? top[active] : null;
  const pct = (cnt) => total > 0 ? (cnt / total) * 100 : 0;

  if (!total || !top.length) {
    return (
      <Card {...wrapperProps}>
        <CardTitle style={{ color: "#FFC107" }}>Leads por tag</CardTitle>
        <div className="muted">Sem dados para o período.</div>
      </Card>
    );
  }

  return (
    <Card {...wrapperProps}>
      <CardTitle style={{ color: "#FFC107" }}>Leads por tag</CardTitle>

      <div className="tags__wrap">
        {/* Donut (sem legenda à esquerda) */}
        <div className="tags__viz">
          <div className="donut__box">
            <ResponsiveContainer width="100%" height={DONUT.height}>
              <PieChart>
                <RTooltip
                  contentStyle={{ background:"#2a2a37", border:"none", borderRadius:8, color:"#fff" }}
                  formatter={(v, name) => [fmt(v), name]}   // mostra o NOME correto da fatia
                />
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={DONUT.inner}
                  outerRadius={DONUT.outer}
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={1.5}
                  stroke="rgba(0,0,0,.35)"
                  strokeWidth={DONUT.strokeW}
                  cornerRadius={3}
                  onMouseEnter={(_, i) => setActive(i)}
                  onMouseLeave={() => setActive(-1)}
                >
                  {pieData.map((e, i) => (
                    <Cell key={e.name} fill={e.color} opacity={active < 0 || active === i ? 1 : .45} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {/* rótulo central */}
            <div className="donut__center">
              <div className="donut__main">{activeRow ? fmt(activeRow.count) : fmt(total)}</div>
              <div className="donut__sub muted">{activeRow ? activeRow.name : "leads"}</div>
            </div>
          </div>
        </div>

        {/* Lista à direita (bolinha colorida + métricas) */}
        <div className="tags__list">
          {top.map((r) => (
            <div key={r.name} className="tag__row">
              <span className="tag-dot" style={{ background: getColor(r.name) }} />
              <div className="tag__name" title={r.name}>{r.name}</div>
              <div className="tag__count">{fmt(r.count)}</div>
              <div className="tag__pct muted">{pct(r.count).toFixed(1)}%</div>
              <span className={`chip chip--mini ${(r.delta_pp || 0) >= 0 ? "chip--up" : "chip--down"}`}>
                {(r.delta_pp || 0) >= 0 ? "▲" : "▼"} {pp(Math.abs(r.delta_pp || 0))}
              </span>
              <div className="tag__bar">
                <div style={{ width: `${Math.min(100, pct(r.count))}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="muted" style={{ marginTop: 8 }}>
        Total: <strong>{fmt(total)}</strong> leads
      </div>
    </Card>
  );
}


/* ========= CARD: Meta – Investimento, CPL e Top Anúncios ========= */
function MetaMiniBoard({
  meta: metaFromProps,          // pode vir pronto do dashboard2.php (campo "meta_mini")
  tipoUsuario,                  // "master" vê o seletor interno
  usuarios = [],                // lista (id, nome_empresa) para o seletor
  defaultUsuarioId = "",        // id selecionado no filtro global (opcional)
  adAccountId,                  // fallback: IDs salvos/local se o PHP não tiver enviado meta_mini
  pageId,
  igUserId,
  campaignId,
  wrapperProps = {}
}) {
  const [loading, setLoading] = React.useState(true);
  const [err, setErr]         = React.useState("");
  const [meta, setMeta]       = React.useState(metaFromProps || null);

  // --- seletor interno (apenas master) ---
  const [userOverride, setUserOverride] = React.useState(defaultUsuarioId);
  React.useEffect(() => setUserOverride(defaultUsuarioId), [defaultUsuarioId]);

  const fetchMiniForUser = React.useCallback(async (uid) => {
    setLoading(true); setErr("");
    try {
      const qs = new URLSearchParams();
      if (uid) qs.set("usuario_id", String(uid));
      const r = await axios.get(
        `https://welaunchyou.com.br/crm-wly/dashboard.php?${qs.toString()}`,
        { withCredentials: true }
      );
      const mini = r.data?.meta_mini;
      if (!mini) throw new Error("Sem dados para este usuário.");
      setMeta(mini);
      // se backend sinalizar não conectado:
      if (mini.connected === false) setErr(mini.reason || "Nenhum vínculo Meta para este usuário.");
    } catch (e) {
      setErr(e?.message || "Erro ao carregar.");
    } finally {
      setLoading(false);
    }
  }, []);

  // 1) Se o PHP já trouxe "meta_mini", uso direto
  React.useEffect(() => {
    if (metaFromProps) {
      setMeta(metaFromProps);
      setErr(metaFromProps.connected === false ? (metaFromProps.reason || "Conta Meta não conectada.") : "");
      setLoading(false);
    }
  }, [metaFromProps]);

  // 2) Fallback: só busca no endpoint se NÃO veio pelo PHP
  React.useEffect(() => {
    if (metaFromProps) return; // já temos os dados do backend principal

    const defaults = (() => {
      try { return JSON.parse(localStorage.getItem("meta_defaults") || "{}"); } catch { return {}; }
    })();
    const acct = adAccountId || defaults.ad_account_id;
    const pid  = pageId       || defaults.page_id;
    const igid = igUserId     || defaults.ig_user_id;

    if (!acct || !pid || !igid) {
      setErr("Conecte sua conta Meta para ver este card.");
      setLoading(false);
      return;
    }

    const qs = new URLSearchParams({
      ad_account_id: acct,
      page_id: pid,
      ig_user_id: igid,
      data_inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
      data_fim: new Date().toISOString().slice(0, 10),
    });
    if (campaignId) qs.set("campaign_id", campaignId);

    (async () => {
      try {
        setLoading(true);
        const r = await fetch(`https://welaunchyou.com.br/crm-wly/api/integracoes/meta_relatorio.php?${qs}`);
        const j = await r.json();
        if (!j?.success) throw new Error(j?.error || "Falha no relatório Meta");
        setMeta(j.data || {});
      } catch (e) {
        setErr(e.message || "Erro ao carregar Meta");
      } finally {
        setLoading(false);
      }
    })();
  }, [metaFromProps, adAccountId, pageId, igUserId, campaignId]);

  // === Decisão de origem dos dados (mini pronto vs. bruto da API) ===
  const useMini = !!meta?.investimento; // quando veio do PHP (meta_mini)

  // === KPIs ===
  const spendTotal = React.useMemo(() => {
    if (useMini) return Number(meta.investimento.total || 0);
    const rows = meta?.meta_ads?.data || [];
    return rows.reduce((s, r) => s + Number(r.spend || 0), 0);
  }, [meta, useMini]);

  const messagesTotal = React.useMemo(() => {
    if (useMini) return Number(meta?.cpl?.msgs || 0);
    // tenta via conjunto "campaign" e cai para "ads" se preciso
    const fromCampaign = (meta?.meta_ads_conversas?.data || []).reduce((s, r) => s + pickAction(r.actions), 0);
    if (fromCampaign > 0) return fromCampaign;
    return (meta?.meta_ads?.data || []).reduce((s, r) => s + pickAction(r.actions), 0);
  }, [meta, useMini]);

  const cpl = React.useMemo(() => {
    if (useMini) return Number(meta?.cpl?.value || 0);
    return messagesTotal > 0 ? spendTotal / messagesTotal : 0;
  }, [useMini, meta, messagesTotal, spendTotal]);

  // === Sparklines ===
  const investSeries = React.useMemo(() => {
    if (useMini && meta?.investimento?.spark?.labels) {
      const L = meta.investimento.spark.labels, D = meta.investimento.spark.data || [];
      return L.map((x, i) => ({ x, y: Number(D[i] || 0) }));
    }
    const s = buildDailySeries(meta?.meta_ads_daily, "spend");
    if (s.length) return s;
    const r = buildDailySeries(meta?.meta_ads_daily, "reach");
    return r.map(p => ({ x: p.x, y: p.y })); // proxy visual
  }, [meta, useMini]);

  const cplSeries = React.useMemo(() => {
    if (useMini && meta?.cpl?.spark?.labels) {
      const L = meta.cpl.spark.labels, D = meta.cpl.spark.data || [];
      return L.map((x, i) => ({ x, y: Number(D[i] || 0) }));
    }
    const spendByDay = buildDailySeries(meta?.meta_ads_daily, "spend");
    const msgsByDay  = buildDailyMessages(meta?.meta_ads_daily);
    if (!spendByDay.length || !msgsByDay.length) return [];
    const mapSpend = new Map(spendByDay.map(p => [p.x, p.y]));
    return msgsByDay
      .filter(p => mapSpend.has(p.x) && p.y > 0)
      .map(p => ({ x: p.x, y: mapSpend.get(p.x) / p.y }));
  }, [meta, useMini]);

  // === Top anúncios ===
  const topAds = React.useMemo(() => {
    if (useMini && Array.isArray(meta?.top_ads?.list)) {
      return meta.top_ads.list
        .map(a => ({
          id: a.ad_id,
          name: a.ad_name || "(Sem nome)",
          msg: Number(a.messages || 0),
          spend: Number(a.spend || 0),
          cpl: Number(a.cpl || 0),
        }))
        .slice(0, 5);
    }
    const rows = meta?.meta_ads_ads?.data || [];
    const arr = rows
      .map(r => ({
        id: r.ad_id,
        name: r.ad_name || "(Sem nome)",
        msg: pickAction(r.actions),
        spend: Number(r.spend || 0),
      }))
      .filter(x => x.msg > 0);
    arr.sort((a, b) => b.msg - a.msg);
    return arr.slice(0, 5);
  }, [meta, useMini]);

  const top1 = topAds[0];

  return (
    <Card {...wrapperProps}>
      <CardTitle
        style={{
          color: "#FFC107",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span>Meta (Investimento, CPL e Top anúncios)</span>

        {tipoUsuario === "master" && usuarios?.length > 0 && (
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="muted" style={{ fontSize: 12 }}>Ver dados de:</span>
            <select
              value={userOverride}
              onChange={(e) => {
                const uid = e.target.value;
                setUserOverride(uid);
                fetchMiniForUser(uid);
              }}
              style={{
                background: "#2a2a37",
                color: "#fff",
                border: "1px solid #3a3a47",
                borderRadius: 8,
                padding: "6px 10px",
                fontSize: 12,
              }}
              title="Carregar Meta deste usuário"
            >
              <option value="">Todos</option>
              {[...usuarios]
                .sort((a, b) => (a.nome_empresa || "").localeCompare(b.nome_empresa || ""))
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome_empresa}
                  </option>
                ))}
            </select>
          </label>
        )}
      </CardTitle>

      {loading && <div className="muted">Carregando…</div>}
      {!loading && err && <div className="muted" style={{ color: "#F87171" }}>{err}</div>}

      {!loading && !err && (
        <div className="meta3__wrap">
          {/* KPIs à esquerda */}
          <div className="meta3__kpis">
            <div className="kpiBlock">
              <div className="kpiTitle">Investimento no mês</div>
              <div className="kpiValue">
                R$ {spendTotal.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="kpiSpark">
                <ResponsiveContainer width="100%" height={48}>
                  <LineChart data={investSeries}>
                    <XAxis dataKey="x" hide />
                    <YAxis hide />
                    <RTooltip
                      contentStyle={TOOLTIP_STYLE}
                      formatter={(v) => [`R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`, "Dia"]}
                    />
                    <Line type="monotone" dataKey="y" stroke="#29B6F6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {!buildDailySeries(meta?.meta_ads_daily, "spend").length && !useMini && (
                <div className="muted mini-note">linha baseada em alcance diário (proxy visual)</div>
              )}
            </div>

            <div className="kpiBlock">
              <div className="kpiTitle">CPL do mês</div>
              <div className="kpiValue">
                R$ {cpl.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              {cplSeries.length ? (
                <div className="kpiSpark">
                  <ResponsiveContainer width="100%" height={48}>
                    <LineChart data={cplSeries}>
                      <XAxis dataKey="x" hide />
                      <YAxis hide />
                      <RTooltip
                        contentStyle={TOOLTIP_STYLE}
                        formatter={(v) => [`R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`, "Dia"]}
                      />
                      <Line type="monotone" dataKey="y" stroke="#7CB342" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                !useMini && <div className="muted mini-note">sem detalhamento diário de ações para sparkline</div>
              )}
            </div>
          </div>

          {/* Top anúncios à direita */}
          <div className="meta3__ads">
            {top1 ? (
              <div className="ad__hero" title={top1.name}>
                <div className="ad__hero__title">Mais mensagens</div>
                <div className="ad__hero__name">{top1.name}</div>
                <div className="ad__hero__metrics">
                  <span className="chip chip--mini">Mensagens: {top1.msg}</span>
                  <span className="chip chip--mini">
                    Invest.: R$ {Number(top1.spend || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="ad__hero__bar">
                  <div style={{ width: "100%" }} />
                </div>
              </div>
            ) : (
              <div className="muted">Sem anúncios com mensagens.</div>
            )}

            <div className="ads__list">
              {topAds.map((a, i) => (
                <div key={a.id || i} className="ad__row">
                  <div className="ad__name" title={a.name}>
                    {i + 1}. {a.name}
                  </div>
                  <div className="ad__msg">{a.msg}</div>
                  <div className="ad__bar">
                    <div style={{ width: `${Math.min(100, (a.msg / (top1?.msg || 1)) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}



/* ===== Registry + drag ===== */
const DEFAULT_LAYOUT = [
  // 3 + 9 = 12
  { id: "kpiLeads",   span: 3 },
  { id: "ladder",     span: 9 },

  // 8 + 4 = 12
  { id: "metaResumo", span: 8 },
  { id: "origens",    span: 4 },

  // 6 + 3 + 3 = 12

  { id: "acoesHoje",      span: 6 },
  { id: "acoesAtrasadas", span: 6 },

  { id: "tags",           span: 6 },
];


const DEFAULT_SPAN = Object.fromEntries(DEFAULT_LAYOUT.map(i => [i.id, i.span]));

function applyDefaultsToLayout(loaded) {
  const arr = Array.isArray(loaded) ? loaded : [];
  // aceita array de objetos {id, span} ou só ids
  const savedIds = arr.map(x => (typeof x === "string" ? x : x.id)).filter(Boolean);

  const seen = new Set();
  const merged = [];

  // 1) mantém a ordem salva, mas força o span padrão
  for (const id of savedIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    merged.push({ id, span: DEFAULT_SPAN[id] ?? 12 });
  }

  // 2) adiciona cards novos que não existiam no salvo
  for (const def of DEFAULT_LAYOUT) {
    if (!seen.has(def.id)) merged.push({ id: def.id, span: def.span });
  }

  return merged;
}

function DraggableCard({ id, span, onDragStart, onDragEnter, onDragEnd, children }) {
  const canDrag = typeof window !== "undefined" && window.matchMedia("(pointer:fine)").matches;

  return (
    <div
      className="draggable"
      style={{ gridColumn: `span ${span}` }}
      draggable={canDrag}
      onDragStart={(e) => { if(!canDrag) return; e.dataTransfer.setData("text/plain", id); onDragStart(id); }}
      onDragOver={(e) => { if(!canDrag) return; e.preventDefault(); }}
      onDragEnter={() => { if(!canDrag) return; onDragEnter(id); }}
      onDragEnd={() => { if(!canDrag) return; onDragEnd(); }}
    >
      {children}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState({});
  const [tipoUsuario, setTipoUsuario] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState("");

  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [draggingId, setDraggingId] = useState(null);

  const [acoesPage, setAcoesPage] = useState(1);

  const [bootLoading, setBootLoading] = useState(true);    
  const [dataLoading, setDataLoading] = useState(false);    
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const showBlur = dataLoading || !usersLoaded || !dataLoaded;


  const masonryRef = useMasonryRows([layout, data]);

  /* ===== persistência no servidor com fallback local ===== */
  const saveLayoutServer = useCallback(async (l) => {
    try {
      await axios.post(
        "https://welaunchyou.com.br/crm-wly/dashboard_layout.php",
        { layout: l },
        { withCredentials: true }
      );
    } catch {
      try { localStorage.setItem("wly_layout_fallback", JSON.stringify(l)); } catch {}
    }
  }, []);

  const loadLayoutServer = useCallback(async () => {
    try {
      const r = await axios.get("https://welaunchyou.com.br/crm-wly/dashboard_layout.php", { withCredentials: true });
      if (Array.isArray(r.data?.layout)) return r.data.layout;
    } catch {}
    try {
      const raw = localStorage.getItem("wly_layout_fallback");
      if (raw) return JSON.parse(raw);
    } catch {}
    return DEFAULT_LAYOUT;
  }, []);

useEffect(() => {
  loadLayoutServer().then((loaded) => {
    const merged = applyDefaultsToLayout(loaded);
    setLayout(merged);

    // persiste se diferiu do que veio
    const raw = Array.isArray(loaded) ? loaded : [];
    const changed = JSON.stringify(merged) !== JSON.stringify(raw);
    if (changed) {
      try { saveLayoutServer(merged); } catch {}
      try { localStorage.setItem("wly_layout_fallback", JSON.stringify(merged)); } catch {}
    }
  });
}, [loadLayoutServer, saveLayoutServer]);

  const reorder = useCallback((fromId, toId) => {
    setLayout((prev) => {
      const a = [...prev];
      const from = a.findIndex(x => x.id === fromId);
      const to = a.findIndex(x => x.id === toId);
      if (from === -1 || to === -1 || from === to) return prev;
      const [moved] = a.splice(from, 1);
      a.splice(to, 0, moved);
      return a;
    });
  }, []);

  const handleDragStart = (id) => setDraggingId(id);
  const handleDragEnter = (overId) => { if (draggingId && draggingId !== overId) reorder(draggingId, overId); };
  const handleDragEnd = () => { setDraggingId(null); saveLayoutServer(layout); };

  /* ===== dados do dashboard ===== */
  const carregar = async (uid) => {
  const id = uid ?? usuarioSelecionado;
  const qs = new URLSearchParams();
  if (id) qs.set("usuario_id", id);
  qs.set("acoes_pagina", String(acoesPage));
  qs.set("acoes_limite", String(PAGE_SIZE_ACTIONS));

  try {
    setDataLoading(true);        
    setDataLoaded(false);

    const res = await axios.get(
      `https://welaunchyou.com.br/crm-wly/dashboard.php?${qs.toString()}`,
      { withCredentials: true }
    );

    const payload = res.data || {};
    setData(payload);
    setTipoUsuario(payload?.tipo_usuario || "");
    setDataLoaded(true);
  } finally {
    setDataLoading(false);  
  }
};

useEffect(() => {
  (async () => {
    try {
      const r = await axios.get("https://welaunchyou.com.br/crm-wly/listagem_usuarios.php", { withCredentials: true });
      if (r.data?.usuarios) setUsuarios(r.data.usuarios);
    } finally {
      setUsersLoaded(true);
    }
  })();
}, []);

useEffect(() => {
  (async () => {
    setBootLoading(true);
    try {
      await Promise.all([
        carregar(),                       // dados principais
        (async () => {                    // aguarda o usersLoaded virar true
          if (!usersLoaded) {
            // polling leve: espera o efeito acima completar
            while (!usersLoaded) await new Promise(r => setTimeout(r, 20));
          }
        })()
      ]);
    } finally {
      setBootLoading(false);
    }
  })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []); // apenas 1x na montagem


  useEffect(() => { carregar().catch(() => {}); }, [usuarioSelecionado, acoesPage]);

  const leadsSparkSeries = useMemo(() => {
    const labels = data?.leads_spark?.labels || [];
    const values = data?.leads_spark?.data || [];
    return labels.map((x, i) => ({ x, y: Number(values[i] || 0) }));
  }, [data]);

  const totalLeads = Number(data?.total_leads || 0);
  const deltaLeads = Number(data?.delta_leads_mes ?? 0);

  /* concluir ação (AGORA dentro do componente) */
  const concluirAcao = useCallback(async (acaoId) => {
  try {
    const body = new URLSearchParams({ acao_id: String(acaoId), novo_status: "Concluída" });
    await axios.post("https://welaunchyou.com.br/crm-wly/listagem_acoes.php", body, {
      withCredentials: true,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });

    setData((prev) => {
      const next  = { ...(prev || {}) };

      // -------- AÇÕES DE HOJE --------
      if (next.acoes_hoje) {
        const bloco = { ...next.acoes_hoje, items: [...(next.acoes_hoje.items || [])] };
        const idx = bloco.items.findIndex(it => it.id === acaoId);
        if (idx !== -1 && !bloco.items[idx].is_done) {
          bloco.items[idx] = { ...bloco.items[idx], status:"Concluída", is_done:1 };
          bloco.items.sort((a,b) => ((a.is_done?1:0)-(b.is_done?1:0)) || (a.lead_first||"").localeCompare(b.lead_first||"", "pt-BR", {sensitivity:"base"}));
          const c = { ...(bloco.counts || {}) };
          c.pendentes = Math.max(0, (c.pendentes||0)-1);
          c.concluidas = (c.concluidas||0)+1;
          bloco.counts = c;
          next.acoes_hoje = bloco;
        }
      }

      // -------- ATRASADAS --------
      if (next.acoes_atrasadas) {
        const blocoA = { ...next.acoes_atrasadas, items: [...(next.acoes_atrasadas.items || [])] };
        const before = blocoA.items.length;
        blocoA.items = blocoA.items.filter(it => it.id !== acaoId);
        if (blocoA.items.length !== before) {
          const cA = { ...(blocoA.counts || {}) };
          cA.pendentes = Math.max(0, (cA.pendentes||0)-1);
          cA.total     = Math.max(0, (cA.total||0)-1);
          blocoA.counts = cA;
          next.acoes_atrasadas = blocoA;
        }
      }

      return next;
    });
  } catch {}
}, []);



  const renderCardById = (id) => {
    if (id === "kpiLeads") {
      return (
        <KpiCard
          title="Leads no mês"
          value={fmtInt(totalLeads)}
          delta={deltaLeads}
          series={leadsSparkSeries}
          wrapperProps={{ 'data-card': 'kpi-leads', className: 'card--kpi-leads' }}
        />
      );
    }
    if (id === "ladder") {
      return (
        <FunnelLadderCard
          funnel={data?.funnel}
          wrapperProps={{ 'data-card': 'ladder', className: 'card--ladder' }}
        />
      );
    }
    if (id === "acoesHoje") {
      return (
        <ActionsTodayCard
          acoes={data?.acoes_hoje}
          onConcluir={concluirAcao}
          wrapperProps={{ 'data-card': 'actions-today', className: 'card--actions-today' }}
        />
      );
    }

    if (id === "acoesAtrasadas") {
  return (
    <ActionsLateCard
      acoes={data?.acoes_atrasadas}
      onConcluir={concluirAcao}
      wrapperProps={{ 'data-card': 'actions-late', className: 'card--actions-late' }}
    />
  );
}

if (id === "origens") {
  return (
    <LeadsBySourceCard
      data={data?.leads_por_origem}
      wrapperProps={{ 'data-card':'origens', className:'card--origins' }}
    />
  );
}

if (id === "tags") {
  return (
    <LeadsByTagCard
      data={data?.leads_por_tag}
      wrapperProps={{ 'data-card':'tags', className:'card--tags' }}
    />
  );
}

// onde você renderiza o card:
if (id === "metaResumo") {
  return (
    <MetaMiniBoard
      meta={data?.meta_mini}
      tipoUsuario={tipoUsuario}
      usuarios={usuarios}
      defaultUsuarioId={usuarioSelecionado || ""}
      wrapperProps={{ 'data-card': 'meta-resumo', className: 'card--meta3' }}
    />
  );
}


    return null;
  };

return (
  
  <Container className={`main-frame dashboard-page ${showBlur ? "is-loading" : ""}`}>
  <WelcomeMessage style={{ borderBottom: "none" }}>
    Seja bem-vindo(a), {data?.nome_usuario || "Usuário"}!
  </WelcomeMessage>

  {tipoUsuario === "master" && (
    <div className="dash-filtro">
      <label htmlFor="filtroUsuario" className="filtro-usuarios__label">
        Filtrar por usuário:
      </label>

      <select
        id="filtroUsuario"
        className="filtro-usuarios__select"
        value={usuarioSelecionado}
        onChange={(e) => setUsuarioSelecionado(e.target.value)}
      >
        <option value="">Todos</option>
        {[...usuarios]
          .sort((a, b) => (a.nome_empresa ?? "").localeCompare(b.nome_empresa ?? ""))
          .map((u) => (
            <option key={u.id} value={u.id}>
              {u.nome_empresa}
            </option>
          ))}
      </select>

      <button
        type="button"
        onClick={() => carregar(usuarioSelecionado)}
        className="filtro-usuarios__botao"
      >
        Aplicar Filtro
      </button>
    </div>
  )}

  {/* Área rolável do dashboard (corrige mobile) */}
  <div className="dashboard-scroll">
    <Grid
      ref={masonryRef}
      className="dash-grid"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(12, minmax(0,1fr))",
        gap: 16,
        gridAutoRows: 8,
        gridAutoFlow: "dense",
        alignItems: "start",
      }}
    >
      {layout.map((item) => (
        <DraggableCard
          key={item.id}
          id={item.id}
          span={item.span}
          onDragStart={handleDragStart}
          onDragEnter={handleDragEnter}
          onDragEnd={handleDragEnd}
        >
          {renderCardById(item.id)}
        </DraggableCard>
      ))}
    </Grid>

    <div
      className={`page-blur-local ${showBlur ? "show" : ""}`}
      role="status"
      aria-live="polite"
      aria-busy={showBlur}
    >
      <div style={{ textAlign: "center" }}>
        <div className="loader-ring" />
        <div className="loader-text">Carregando…</div>
      </div>
    </div>
  </div>
</Container>


);
}
